import React, { useEffect, useState } from 'react';
import {
  collection, query, where, onSnapshot,
  updateDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { formatTime } from './scheduleUtils';

const fncs = getFunctions();
const notifyMarketplaceDecision = httpsCallable(fncs, 'notifyMarketplaceDecision');

/**
 * ShiftMarketplace — manager/owner panel for pending swap/drop requests.
 * Lists all pending shiftSwapRequests for this restaurant.
 * Approve: updates shift.employeeId to targetEmployeeId (for swaps) or null (for drops).
 * Deny: sets status to "denied".
 */
const ShiftMarketplace = ({ user, restaurantId }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [working, setWorking]   = useState(null); // requestId being processed

  useEffect(() => {
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'shiftSwapRequests'),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [restaurantId]);

  const review = async (request, decision) => {
    setWorking(request.id);
    const byName = `${user.firstName} ${user.lastName}`;
    try {
      const reqRef = doc(db, 'restaurants_data', restaurantId, 'shiftSwapRequests', request.id);
      await updateDoc(reqRef, {
        status: decision,
        reviewedBy: user.accountId,
        reviewedByName: byName,
        reviewedAt: serverTimestamp(),
      });

      if (decision === 'approved') {
        const shiftRef = doc(
          db, 'restaurants_data', restaurantId,
          'schedules', request.scheduleId,
          'shifts', request.shiftId
        );
        if (request.type === 'drop') {
          await updateDoc(shiftRef, {
            employeeId: null,
            employeeName: '',
            isOpenShift: true,
            status: 'scheduled',
            updatedAt: serverTimestamp(),
            updatedBy: user.accountId,
            updatedByName: byName,
          });
        } else if (request.type === 'claim') {
          // Assign the open shift to the employee who claimed it
          await updateDoc(shiftRef, {
            employeeId:    request.requesterId,
            employeeName:  request.requesterName,
            isOpenShift:   false,
            status:        'covered',
            updatedAt:     serverTimestamp(),
            updatedBy:     user.accountId,
            updatedByName: byName,
          });
        } else {
          // swap
          await updateDoc(shiftRef, {
            employeeId:   request.targetEmployeeId,
            employeeName: request.targetEmployeeName || '',
            isOpenShift:  false,
            status:       'covered',
            updatedAt:    serverTimestamp(),
            updatedBy:    user.accountId,
            updatedByName: byName,
          });
        }
      }

      // Send notification email(s) — non-fatal, don't block the review action
      notifyMarketplaceDecision({ restaurantId, requestId: request.id }).catch((err) =>
        console.warn('Marketplace notification failed (non-fatal):', err)
      );
    } catch (err) {
      console.error('Marketplace review failed:', err);
    } finally {
      setWorking(null);
    }
  };

  if (loading) return <p className="text-sm text-neutral-400 py-8 text-center">Loading requests…</p>;

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-neutral-200 rounded-xl">
        <p className="text-sm font-medium text-neutral-400">No pending requests</p>
        <p className="text-sm text-neutral-300 mt-1">Swap and drop requests from your team will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map(req => (
        <div key={req.id} className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-sm font-semibold px-2 py-0.5 rounded-full border ${
                req.type === 'claim'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : req.type === 'swap'
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {req.type === 'claim' ? 'Claim' : req.type === 'swap' ? 'Swap' : 'Drop'}
              </span>
              <span className="text-sm text-neutral-400">
                {req.createdAt?.toDate
                  ? req.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : ''}
              </span>
            </div>
            <p className="text-sm font-medium text-neutral-800">
              <span className="font-semibold">{req.requesterName}</span>
              {req.type === 'claim'
                ? <> wants to claim the open <span className="font-semibold">{req.shiftRole}</span> shift
                    {req.shiftDate ? ` on ${new Date(req.shiftDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}` : ''}</>
                : req.type === 'swap'
                ? <> wants to swap with <span className="font-semibold">{req.targetEmployeeName || 'anyone'}</span></>
                : <> wants to drop their shift</>
              }
            </p>
            <p className="text-sm text-neutral-500 mt-0.5">Week: {req.scheduleId}</p>
            {req.requesterNote && (
              <p className="text-sm text-neutral-500 mt-1 italic">"{req.requesterNote}"</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => review(req, 'denied')}
              disabled={working === req.id}
              className="px-4 py-1.5 text-sm font-medium border border-neutral-300 text-neutral-600 rounded-lg hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              Deny
            </button>
            <button
              onClick={() => review(req, 'approved')}
              disabled={working === req.id}
              className="px-4 py-1.5 text-sm font-medium bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors disabled:opacity-50"
            >
              {working === req.id ? '…' : 'Approve'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ShiftMarketplace;
